import { RowSelectionState } from '@tanstack/react-table'
import { useCallback, useState } from 'react'

import { TableSelectionRef } from './useTableSelectionRef'

export interface TableSelectionProps<T> {
  /** Row selection */
  enableRowSelection?: boolean
  /** Uncontrolled mode: initial selection */
  defaultRowSelection?: RowSelectionState
  /** Controlled mode: current selection state */
  rowSelection?: RowSelectionState
  /** Controlled mode: callback when selection changes */
  onRowSelectionChange?: (selection: RowSelectionState) => void
  getRowId?: (row: T) => string

  ref?: React.Ref<TableSelectionRef<T>>
}

interface UseTableSelectionProps {
  rowSelection?: RowSelectionState
  onRowSelectionChange?: (selection: RowSelectionState) => void
  defaultRowSelection?: RowSelectionState
}

interface UseTableSelectionReturn {
  rowSelection: RowSelectionState
  setRowSelection: (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => void
  isControlled: boolean
}

export function useTableSelection({
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  defaultRowSelection = {},
}: UseTableSelectionProps): UseTableSelectionReturn {
  const isControlled = controlledRowSelection !== undefined

  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>(defaultRowSelection)

  const rowSelection = isControlled ? controlledRowSelection : internalRowSelection

  const setRowSelection = useCallback(
    (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      const newValue = typeof updater === 'function' ? updater(rowSelection) : updater

      if (isControlled) {
        onRowSelectionChange?.(newValue)
      } else {
        setInternalRowSelection(newValue)
      }
    },
    [isControlled, onRowSelectionChange, rowSelection],
  )

  return {
    rowSelection,
    setRowSelection,
    isControlled,
  }
}
